<script lang="ts">
  import { onMount } from 'svelte';
  import {
    type SingleStamp,
    type PhilatelicSet,
    type StampFromSet,
    PhilatelicItemType,
  } from '@briefmarken_katalog/types';
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogDescription,
  } from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import { Input } from '$lib/components/ui/input';
  import { Label } from '$lib/components/ui/label';
  import { t } from '$lib/i18n';
  import SingleStampRow from '../itemlist/SingleStampRow.svelte';
  import PhilatelicSetRow from '../itemlist/PhilatelicSetRow.svelte';
  import ContainedStampsList, { loadCatalog } from '../itemlist/ContainedStampsList.svelte';

  type LoadedItem =
    | { kind: 'single'; stamp: SingleStamp }
    | { kind: 'set'; set: PhilatelicSet };

  let { stampIds = $bindable<string[]>([]), isReadonly = false, invalid = false }: {
    stampIds: string[];
    isReadonly?: boolean;
    invalid?: boolean;
  } = $props();

  let loadedItems = $state<LoadedItem[]>([]);
  let loading = $state(true);
  let dialogOpen = $state(false);
  let dateFrom = $state('');
  let dateTo = $state('');

  let visibleItems = $derived(loadedItems.filter(item => {
    const date = new Date(item.kind === 'single' ? item.stamp.date_of_issue : item.set.date_of_issue);
    if (dateFrom && date < new Date(dateFrom)) return false;
    if (dateTo   && date > new Date(dateTo))   return false;
    return true;
  }));

  onMount(async () => {
    try {
      const items = await loadCatalog();
      if (!items) return;
      const list: LoadedItem[] = [];
      for (const item of items) {
        if (item.type === PhilatelicItemType.SingleStamp) {
          list.push({ kind: 'single', stamp: item as SingleStamp });
        } else if (item.type === PhilatelicItemType.Set) {
          list.push({ kind: 'set', set: item as PhilatelicSet });
        }
      }
      loadedItems = list;
    } finally {
      loading = false;
    }
  });

  function toggleStamp(id: string) {
    if (stampIds.includes(id)) {
      stampIds = stampIds.filter(s => s !== id);
    } else {
      stampIds = [...stampIds, id];
    }
  }

  function getSetStampIds(set: PhilatelicSet): string[] {
    return set.stamps.map(s => String(s._id)).filter(Boolean);
  }

  function toggleSet(set: PhilatelicSet) {
    const ids = getSetStampIds(set);
    const allSelected = ids.length > 0 && ids.every(id => stampIds.includes(id));
    if (allSelected) {
      stampIds = stampIds.filter(id => !ids.includes(id));
    } else {
      stampIds = [...new Set([...stampIds, ...ids])];
    }
  }

  function isSetSelected(set: PhilatelicSet): boolean {
    const ids = getSetStampIds(set);
    return ids.length > 0 && ids.every(id => stampIds.includes(id));
  }

  function isSetIndeterminate(set: PhilatelicSet): boolean {
    const ids = getSetStampIds(set);
    return ids.some(id => stampIds.includes(id)) && !ids.every(id => stampIds.includes(id));
  }

  function indeterminate(node: HTMLInputElement, value: boolean): { update: (v: boolean) => void } {
    node.indeterminate = value;
    return { update(v) { node.indeterminate = v; } };
  }
</script>

<div class="grid grid-cols-4 items-start gap-4">
  <Label class={`text-right pt-2${invalid ? ' text-destructive' : ''}`}>{$t('editor.selectStamps')}</Label>
  <div class="col-span-3 flex flex-col gap-2">
    {#if isReadonly}
      <ContainedStampsList {stampIds} />
    {:else}
      <div class="flex flex-col gap-2">
        <Button variant="outline" onclick={() => dialogOpen = true} disabled={loading}>
          {$t('editor.selectStamps')}
        </Button>
        {#if stampIds.length > 0}
          <ContainedStampsList {stampIds} />
        {/if}
      </div>
    {/if}
  </div>
</div>

<Dialog bind:open={dialogOpen}>
  <DialogContent maxWidth="75vw">
    <DialogTitle>{$t('editor.selectStamps')}</DialogTitle>
    <DialogDescription></DialogDescription>
    <div class="flex gap-2 items-center">
      <Input type="date" bind:value={dateFrom} class="flex-1" title={$t('search.dateFrom')} placeholder={$t('search.dateFrom')}/>
      <span class="text-xs text-muted-foreground shrink-0">–</span>
      <Input type="date" bind:value={dateTo} class="flex-1" title={$t('search.dateTo')} placeholder={$t('search.dateTo')}/>
    </div>
    <div class="overflow-y-auto max-h-[60vh] border rounded-md p-2 flex flex-col gap-1">
      {#if loading}
        <span class="text-sm text-muted-foreground">…</span>
      {:else if loadedItems.length === 0}
        <span class="text-sm text-muted-foreground">No accepted stamps in catalog yet.</span>
      {:else}
        {#each visibleItems as item}
          {#if item.kind === 'single'}
            <SingleStampRow stamp={item.stamp} readonly={true}>
              {#snippet leading()}
                <input
                  type="checkbox"
                  checked={stampIds.includes(String(item.stamp._id))}
                  onchange={() => toggleStamp(String(item.stamp._id))}
                  class="w-4 h-4 shrink-0 self-center mr-1 cursor-pointer"
                />
              {/snippet}
            </SingleStampRow>
          {:else}
            <PhilatelicSetRow set={item.set} readonly={true}>
              {#snippet setLeading()}
                <input
                  type="checkbox"
                  checked={isSetSelected(item.set)}
                  onchange={() => toggleSet(item.set)}
                  use:indeterminate={isSetIndeterminate(item.set)}
                  class="w-4 h-4 shrink-0 self-center mr-1 cursor-pointer"
                />
              {/snippet}
              {#snippet stampLeading(stamp: StampFromSet)}
                <input
                  type="checkbox"
                  checked={stampIds.includes(String(stamp._id))}
                  onchange={() => toggleStamp(String(stamp._id))}
                  class="w-4 h-4 shrink-0 self-center mr-1 cursor-pointer"
                />
              {/snippet}
            </PhilatelicSetRow>
          {/if}
        {/each}
      {/if}
    </div>
    <DialogFooter>
      <Button onclick={() => dialogOpen = false}>{$t('editor.done')}</Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
